/**
 * Departments index — landing page that lists every portfolio
 * (department) with KPI counts. Click-through opens the per-department
 * detail page at /departments/:slug.
 *
 * 'Department' = the `portfolio` field on Project. Today these values
 * come from manual tagging on the project record. Projects with no
 * portfolio assigned are surfaced as a separate 'Unassigned' tile so
 * the tagging gap stays visible.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Building2, ArrowRight, Inbox, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "@/lib/roles";
import { useAddDepartment, useDepartments } from "@/hooks/useDepartments";
import { motion } from "framer-motion";
import { TopBar } from "@/components/layout/TopBar";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useSupportQueue } from "@/hooks/useSupportQueue";
import { cn } from "@/lib/cn";
import { kpiBucketsFromProjects, type KpiBuckets } from "@/lib/departmentKpis";

const UNASSIGNED_KEY = "__unassigned";

interface PortfolioRow {
  key: string;            // URL slug ("__unassigned" or encoded name)
  label: string;          // human-readable
  projects: number;       // total count
  kpis: KpiBuckets;
}

export function Departments() {
  const { data, isLoading } = usePortfolio(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Departments exist on their own now (2026-10-22): a department with no
  // projects yet still gets a tile, so a new team sees itself here on day one.
  const departmentsQ = useDepartments();
  const { isPmo } = useCan();

  const rows = useMemo<PortfolioRow[]>(() => {
    const list = Array.isArray(data) ? data : [];
    const buckets = new Map<string, typeof list>();
    for (const d of departmentsQ.data ?? []) buckets.set(d.name, []);
    for (const p of list) {
      const department = (p.department ?? "").trim();
      const key = department || UNASSIGNED_KEY;
      const arr = buckets.get(key) ?? [];
      arr.push(p);
      buckets.set(key, arr);
    }
    const out: PortfolioRow[] = [];
    for (const [key, projects] of buckets.entries()) {
      out.push({
        key,
        label: key === UNASSIGNED_KEY ? "Unassigned" : key,
        projects: projects.length,
        kpis: kpiBucketsFromProjects(projects),
      });
    }
    // Sort: real portfolios alphabetical, Unassigned last
    out.sort((a, b) => {
      if (a.key === UNASSIGNED_KEY) return 1;
      if (b.key === UNASSIGNED_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
    return out;
  }, [data, departmentsQ.data]);

  // Untriaged enhancement requests moved to Triage (2026-08-14) — this
  // index only points at them so stuck items stay visible from here.
  const supportQ = useSupportQueue();
  const untriagedCount = useMemo(() => {
    const items = supportQ.data ?? [];
    return items.filter(
      (i) =>
        !(i.is_closed || i.done_manual || i.jira_status_category === "done") &&
        !(i.department ?? "").trim(),
    ).length;
  }, [supportQ.data]);

  return (
    <>
      <TopBar
        title="Departments"
        subtitle="Per-department views — projects and each department's enhancement queue."
      />
      <div className="p-8">
        {isPmo && <AddDepartmentBar />}
        {untriagedCount > 0 && (
          <Link
            to="/top-projects?view=enhancements"
            className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300 transition-colors hover:bg-amber-50 dark:hover:bg-amber-500/15"
          >
            <Inbox className="h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{untriagedCount}</strong> enhancement request{untriagedCount === 1 ? "" : "s"}{" "}
              awaiting a department — route them on Small Enhancements
            </span>
            <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0" />
          </Link>
        )}
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading…
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-line-strong bg-surface p-12 text-center text-sm text-ink-muted">
            No projects yet.
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row, i) => (
            <motion.button
              key={row.key}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              onClick={() =>
                navigate(`/departments/${row.key}`, {
                  state: { from: location.pathname + location.search },
                })
              }
              className={cn(
                "group flex flex-col gap-3 rounded-xl border bg-surface p-5 text-left transition-all",
                "border-line hover:border-brand-navy-300 hover:shadow-md",
                row.key === UNASSIGNED_KEY && "border-dashed border-line-strong",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg",
                      row.key === UNASSIGNED_KEY
                        ? "bg-raised text-ink-muted"
                        : "bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-600 dark:text-brand-navy-400",
                    )}
                  >
                    <Building2 className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-semibold text-ink">{row.label}</h3>
                </div>
                <ArrowRight className="h-4 w-4 text-ink-faint transition-colors group-hover:text-brand-navy-500" />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-ink-subtle">
                {row.projects === 0 && row.key !== UNASSIGNED_KEY ? "No projects yet" : `${row.projects} project${row.projects === 1 ? "" : "s"}`}
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-md bg-canvas px-2 py-1.5">
                  <div className="font-semibold tabular-nums text-ink-2">
                    {row.kpis.notStarted}
                  </div>
                  <div className="text-ink-muted">Not started</div>
                </div>
                <div className="rounded-md bg-brand-navy-50 dark:bg-brand-navy-500/10 px-2 py-1.5">
                  <div className="font-semibold tabular-nums text-brand-navy-700 dark:text-brand-navy-300">
                    {row.kpis.inProgress}
                  </div>
                  <div className="text-brand-navy-600 dark:text-brand-navy-400">In progress</div>
                </div>
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1.5">
                  <div className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                    {row.kpis.complete}
                  </div>
                  <div className="text-emerald-600 dark:text-emerald-400">Complete</div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </>
  );
}


/** PMO: add a department here, before it has a project. Rename / remove
 *  live on Admin → Departments. */
function AddDepartmentBar() {
  const [name, setName] = useState("");
  const add = useAddDepartment();
  const submit = () => {
    const v = name.trim();
    if (!v) return;
    add.mutate(v, {
      onSuccess: () => { toast.success(`${v} added`); setName(""); },
      onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Couldn't add the department"),
    });
  };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3" data-testid="add-department">
      <Building2 className="h-4 w-4 text-ink-subtle" />
      <span className="text-sm text-ink-2">New department</span>
      <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="e.g. IT Infrastructure" aria-label="New department name"
        className="w-64 rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-ink focus:border-brand-navy-400 focus:outline-none" />
      <button onClick={submit} disabled={add.isPending || !name.trim()} className="inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-50">
        {add.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
      </button>
      <Link to="/admin#departments" className="ml-auto text-xs text-brand-navy-600 dark:text-brand-navy-300 hover:underline">Rename or remove on Admin</Link>
    </div>
  );
}
